import { Box, Collapse, Icon, IconButton, styled } from '@mui/material';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import useAnalytics from '@/hooks/use-analytics';

const INTERACTIVE_HEADER_SELECTOR =
  'input, textarea, select, button, a, label, [role="button"], [role="link"], [contenteditable="true"], [data-expandable-ignore-toggle="true"]';

interface ExpandableContainerProps {
  index?: number;
  name: string;
  expanded?: boolean;
  onToggle?: (index: number) => void;
  summary: React.ReactNode;
  details: React.ReactNode;
}

interface StyledButtonProps {
  open: boolean;
}

export const ExpandableContainer: React.FC<ExpandableContainerProps> = ({
  summary,
  details,
  index,
  name,
  expanded,
  onToggle,
}) => {
  const { trackEvent } = useAnalytics();
  const controlId = `${name.toLocaleLowerCase().replace(/\s+/g, '-')}-content`;
  // Determine if this is controlled by expandable list component
  const isControlled = index !== undefined && onToggle !== undefined;
  // Only use internal state when component is uncontrolled (standalone)
  const [isExpanded, setIsExpanded] = useState(true);
  const prevExpandedState = useRef<boolean | undefined>(undefined);

  const handleToggle = () => {
    if (isControlled) {
      onToggle(index);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const handleHeaderClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target;

    if (target instanceof Element && target.closest(INTERACTIVE_HEADER_SELECTOR)) {
      return;
    }

    handleToggle();
  };

  useEffect(() => {
    const currentState = isControlled ? expanded : isExpanded;

    // Only track if the state has actually changed from a previous known state
    if (prevExpandedState.current !== undefined && prevExpandedState.current !== currentState) {
      if (currentState) {
        trackEvent(`EXPANDABLE_CONTAINER_EXPANDED`);
      } else {
        trackEvent(`EXPANDABLE_CONTAINER_COLLAPSED`);
      }
    }

    prevExpandedState.current = currentState;
  }, [trackEvent, expanded, isExpanded, isControlled]);

  return (
    <StyledContainer
      role={isControlled ? 'listitem' : undefined}
      aria-label={name}
    >
      <StyledBox onClick={handleHeaderClick}>
        <StyledSummary>{summary}</StyledSummary>

        {/* Icon remains clickable while header clicks also toggle. */}
        <StyledIconButton
          onClick={handleToggle}
          aria-expanded={isControlled ? expanded : isExpanded}
          aria-controls={controlId}
          aria-label={`expand-${controlId}`}
          open={isControlled && expanded ? expanded : isExpanded}
        >
          <Icon>expand_more</Icon>
        </StyledIconButton>
      </StyledBox>
      <StyledCollapse
        id={controlId}
        in={isControlled ? expanded : isExpanded}
        timeout={300}
        easing={{
          enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
          exit: 'cubic-bezier(0.4, 0, 0.6, 1)',
        }}
      >
        <CollapseContent>{details}</CollapseContent>
      </StyledCollapse>
    </StyledContainer>
  );
};

const StyledSummary = styled('div')`
  flex-grow: 1;
`;

const StyledIconButton = styled(IconButton)<StyledButtonProps>`
  color: ${({ theme }) => theme.palette.text.secondary};
  outline: 1px solid ${({ theme }) => theme.palette.text.secondary};
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  margin-left: ${({ theme }) => theme.spacing(4)};
`;

const StyledContainer = styled('div')`
  border: 1px solid ${({ theme }) => theme.palette.border.secondary};
  border-radius: var(--border-radius-medium);
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.background.paper};
  will-change: height;
  contain: layout;
`;

const StyledBox = styled(Box)`
  display: flex;
  padding: ${({ theme }) => theme.spacing(3)};
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const StyledCollapse = styled(Collapse)`
  border-top: 1px solid ${({ theme }) => theme.palette.border.secondary};
  will-change: height;
  transform: translateZ(0); /* Force hardware acceleration */
`;

const CollapseContent = styled('div')`
  padding: ${({ theme }) => theme.spacing(3)};
  contain: layout style;
`;

export type { ExpandableContainerProps };
